import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { SERVER } from '../../appShared';
import { telemetryService } from '../../services/telemetryService';
import { apiService } from '../../services/apiService';
import { useSessionLifecycle } from '../../hooks/useSessionLifecycle';
import { SessionGate } from './SessionGate';

export const SessionGateContainer: React.FC = () => {
  const {
    session,
    sessionLoaded,
    sessionEmailInput,
    setSessionEmailInput,
    sessionCodeInput,
    setSessionCodeInput,
    sessionStage,
    setSessionStage,
    isSessionRequestingCode,
    isSessionSubmitting,
    sessionErrorMessage,
    setSessionErrorMessage,
    sessionHintCode,
    sessionResendSeconds,
    sessionAttempts,
    setSessionAttempts,
    sessionLockUntil,
    setSessionLockUntil,
    setSessionResendSeconds,
    setIsSessionRequestingCode,
    setIsSessionSubmitting,
    setSession,
    setSessionHintCode,
    logout
  } = useAuth();

  const {
    sessionConsentChecked,
    setSessionConsentChecked,
    setPendingConsentAcceptance
  } = usePrivacy();

  const logTelemetry = React.useCallback((event: string, payload?: Record<string, any>, level?: 'info' | 'error', overrideUserId?: string) => {
    telemetryService.track(SERVER, {
      userId: overrideUserId || 'anonymous',
      event,
      payload,
      level
    });
  }, []);

  const { requestSessionCode, verifySessionCode } = useSessionLifecycle({
    server: SERVER,
    sessionResendSeconds,
    sessionLockUntil,
    sessionAttempts,
    sessionConsentChecked,
    logTelemetry,
    setIsSessionRequestingCode,
    setSessionErrorMessage,
    setSessionStage,
    setSessionHintCode,
    setSessionResendSeconds,
    setIsSessionSubmitting,
    setSession,
    setSessionCodeInput,
    setSessionAttempts,
    setSessionLockUntil,
    setPendingConsentAcceptance,
    logout
  });

  const signInWithSocial = React.useCallback(async (provider: 'google' | 'apple', idToken: string) => {
    if (!sessionConsentChecked) {
      setSessionErrorMessage('Please accept the Terms and Privacy Policy to continue.');
      return;
    }

    setIsSessionSubmitting(true);
    setSessionErrorMessage(null);
    try {
      const payload = await apiService.fetchJSON(`${SERVER}/api/session/social`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider, idToken })
      });

      if (payload?.token) {
        const hydrated = {
          userId: payload.userId,
          email: payload.email,
          token: payload.token,
          workspaceRole: payload.workspaceRole || 'owner'
        };
        setSession(hydrated);
        logTelemetry('session_start', { email: payload.email, provider }, 'info', payload?.userId);
        setSessionStage('email');
        setSessionHintCode(null);
        setSessionCodeInput('');
        setSessionAttempts(0);
        setSessionLockUntil(null);
        setPendingConsentAcceptance(true);
      }
    } catch (err: any) {
      console.error('Social sign-in failed', err);
      let message = 'Sign-in failed. Please try again or use email.';
      const rawMessage = err?.message;
      if (typeof rawMessage === 'string' && rawMessage.includes('{')) {
        try {
          const parsed = JSON.parse(rawMessage.replace(/^HTTP \d+:?\s*/i, '').trim());
          if (parsed?.error) {
            message = parsed.error;
          }
        } catch {
          // Fall back to the default message when the server payload is not valid JSON.
        }
      }
      setSessionErrorMessage(message);
    } finally {
      setIsSessionSubmitting(false);
    }
  }, [
    logTelemetry,
    sessionConsentChecked,
    setIsSessionSubmitting,
    setPendingConsentAcceptance,
    setSession,
    setSessionAttempts,
    setSessionCodeInput,
    setSessionErrorMessage,
    setSessionHintCode,
    setSessionLockUntil,
    setSessionStage
  ]);

  return (
    <SessionGate
      sessionLoaded={sessionLoaded}
      session={session}
      sessionStage={sessionStage}
      sessionEmailInput={sessionEmailInput}
      setSessionEmailInput={setSessionEmailInput}
      sessionCodeInput={sessionCodeInput}
      setSessionCodeInput={setSessionCodeInput}
      sessionConsentChecked={sessionConsentChecked}
      setSessionConsentChecked={setSessionConsentChecked}
      sessionErrorMessage={sessionErrorMessage}
      setSessionErrorMessage={setSessionErrorMessage}
      sessionHintCode={sessionHintCode}
      sessionResendSeconds={sessionResendSeconds}
      isSessionRequestingCode={isSessionRequestingCode}
      isSessionSubmitting={isSessionSubmitting}
      setSessionStage={setSessionStage}
      requestSessionCode={requestSessionCode}
      verifySessionCode={verifySessionCode}
      signInWithSocial={signInWithSocial}
    />
  );
};

export default SessionGateContainer;
